import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Observable, throwError  } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { SearchResultsRequest, SearchResultsResponse } from '../models/search.model';
import { APIDomainVersion, ContentType } from '../models/site.model';
import { PreviewService } from './preview.service';

@Injectable({
  providedIn: 'root'
})
export class StrapiService {
  private apiUrl = environment.apiURL + '/api';
  private auth_token = environment.strapiAUTH;
  private headers = new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${this.auth_token}`
  });
  private userToken: string;
  
  constructor(
      private http: HttpClient, 
      private authService: AuthService, 
      private previewService: PreviewService,
      ) {
      this.authService.userToken$.subscribe((token) => {
        this.userToken = token;
      });
    }
/* 
  Strapi basic API example: 
  http://localhost:1337/api/your-content-type-plural?filters[your-slug-field-name][operator]=your-slug-goes-here
*/

  processSecureAPICallGet( url: string ): Observable<any> {
    let dynamicHeaders;
    
    if( this.previewService.isPreviewMode() ) {
      dynamicHeaders = this.headers.set('User-Token', environment.previewOverrideToken);
    }else { 
       dynamicHeaders = this.headers.set('User-Token', this.userToken || '');
    }

    return this.http.get<any>(
      url,
      { headers: dynamicHeaders }
    ).pipe(
      catchError(err => {
        if (err.status === 401) {
          this.authService.logout();
        }
        return throwError(() => err);
      })
    );
  }

  processPublicAPICallGet( url: string ): Observable<any> {
    // Use only the static bearer token, no user token
    return this.http.get<any>(
      url,
      { headers: this.headers }
    ).pipe(
      catchError(err => {
        console.error('Preview API call failed:', err);
        return throwError(() => err);
      })
    );
  }

  processSecureAPICallPost<T = any>(url: string, body: any): Observable<T> {
    let dynamicHeaders;
    if( this.previewService.isPreviewMode() ) {
      dynamicHeaders = this.headers.set('User-Token', environment.previewOverrideToken);
    }else { 
      dynamicHeaders = this.headers.set('User-Token', this.userToken || '');
    }

    return this.http.post<T>(
      url,
      body,
      { headers: dynamicHeaders }
    ).pipe(
      catchError(err => {
        if (err.status === 401) {
          this.authService.logout();
        }
        return throwError(() => err);
      })
    );
  }

  getItemBySlug( contentType: string, slug: string ) : Observable<any> {
    return this.processSecureAPICallGet(`${this.apiUrl}/${contentType}?filters[Slug][$eq]=${slug}`);
  }

  getPage( slug: string ) : Observable<any> {
    let url = `${this.apiUrl}/pages?filters[Slug][$eq]=${slug}&populate=Category`;
    
    // Add status parameter if in preview/draft mode
    if (this.previewService.isDraftMode()) {
      url += '&status=draft';
    }
    
    // Normal authenticated request
    return this.processSecureAPICallGet(url);
  }

  getEndpointDetails( slug: string ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/endpoints?filters[Slug][$eq]=${slug}&populate[0]=DomainVersionActive&populate[1]=Group` );
  }

  getAllDomainVersions() : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/domain-versions` );
  }

  getActiveVersion( slug: string ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/endpoints/activeVersions/${slug}` );
  }

  getSideNavByDomainVersion( domainVersion: APIDomainVersion ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/side-nav/${domainVersion}` );
  }

  getSideNavDocs() : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/pages/docs` );
  }

  getAllGroups( domainVersion: APIDomainVersion ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/endpoints/groups/${domainVersion}` );
  }

  getActiveRecipes( ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/recipes?filters[Active][$eq]=true&populate[0]=Icon&populate[1]=Steps` );
  }

  getCodeSnippet( domainVersion: APIDomainVersion, language:string, slug:string ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/codesnippets/${domainVersion}/${language}/${slug}` );
  }

  getCodelibraries(type:string) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/codelibraries/type/${type}` );
  }

  getAllCodelibraries() : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/codelibraries/librarytype/1` );
  }

  getRecipeBySlug( slug: string ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/recipes?filters[Slug][$eq]=${slug}&populate[0]=Icon&populate[Steps][populate][1]=Endpoint` );
  }

  getRecipesByEndpoint( slug: string ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/recipes/by-endpoint/${slug}` );
  }

  getNotesByStep( noteType: string, step:string ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/notes/step/${step}/${noteType}` );
  }

  getSearchResults(
    domainVersion: APIDomainVersion,
    query: string,
    groupFilter?: Array<string>,
    typeFilter?: Array<string>,
    page: number = 1,
    pageSize: number = 25,
    contentTypePriority: ContentType = 'api'
  ): Observable<SearchResultsResponse> {
    const body: SearchResultsRequest = {
      domainVersion,
      query,
      groupFilter: groupFilter?.length ? groupFilter.join(',') : 'all',
      typeFilter: typeFilter?.length ? typeFilter.join(',') : 'all',
      page,
      pageSize,
      contentTypePriority,
      allowDocs: environment.displayDocs,
    };

    return this.processSecureAPICallPost(`${this.apiUrl}/search-results`, body);
  }

  getEndpointParameters( slug: string, domainVersion: APIDomainVersion ) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/parameter-processed/${slug}/${domainVersion}` );
  }

  getQueryHeaderParams( slug: string, domainVersion: APIDomainVersion) : Observable<any> {
    return this.processSecureAPICallGet( `${this.apiUrl}/parameter-processed/query-header-params/${slug}/${domainVersion}` );
  }

  getEndpointResponses( slug: string, domainVersion:APIDomainVersion ) : Observable<any> {
    const [domainName, versionNumber] = domainVersion.split('_');

    const domain = domainName;           // 'secure'
    const version = parseFloat(versionNumber); // 2.3
    // define url to pull response and param data
    let urlAddition = `/responses?filters[Active]=true&filters[Endpoint][Slug][$eq]=${slug}&filters[DomainVersion][Domain][$eq]=${domain}&filters[DomainVersion][Version][$eq]=${version}&populate[ResponseParameters][populate][Children][populate][Children][populate]=Children`;
    
    // add sorting for params and their children
    urlAddition+=`&populate[ResponseParameters][sort]=Name:asc&populate[ResponseParameters][populate][Children][sort]=Name:asc&populate[ResponseParameters][populate][Children][populate][Children][sort]=Name:asc`;
    
    // execute and return the response
    return this.processSecureAPICallGet( `${this.apiUrl}${urlAddition}` );
  }
    
  login( credentials: { username: string; password: string; token: string; }): Observable<any> {
    return this.http.post (`${this.apiUrl}/user/login`, credentials, { headers: this.headers });
  }

  logout( ): Observable<any> {
    const payload = { token: this.userToken };
    return this.http.post (`${this.apiUrl}/user/logout`, payload, { headers: this.headers });
  }

  sendEmail( formData: any, token: string ) : Observable<any> {
    formData.token = token;
    return this.http.post<any>(`${this.apiUrl}/user/request-access`, formData, { headers: this.headers });
  }

  sendPasswordReset( formData: any, token: string ) : Observable<any> {
    formData.token = token;
    return this.http.post<any>(`${this.apiUrl}/user/reset`, formData, { headers: this.headers });
  }

  
}
